import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { usePublicHolidays } from "../hooks/usePublicHolidays";

const NAVY = "#0A316E";
const ORANGE = "#E56A22";

const MONTHS = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

/** Hafta pazartesi başlar (TR takvim düzeni). */
const WEEKDAYS = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"];

/**
 * Sık girilen açıklamalar. Dini bayramlar blok hâlinde girildiği için gün
 * numarası olmadan önerilir; aralık modunda numaralandırma ayrı seçenektir.
 */
const SUGGESTIONS = [
    "Ramazan Bayramı",
    "Kurban Bayramı",
    "Köprü Günü",
    "İdari Tatil",
    "Olağanüstü Tatil",
];

const styles = `
  .holiday-calendar .month-card {
    border: 1px solid #e9edf5; border-radius: 14px; background: #fff; padding: 12px;
  }
  .holiday-calendar .month-title {
    font-weight: 600; color: ${NAVY}; font-size: 0.95rem; margin-bottom: 8px;
  }
  .holiday-calendar .day-grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;
  }
  .holiday-calendar .weekday {
    text-align: center; font-size: 0.68rem; color: #8a94a6; padding: 2px 0; font-weight: 600;
  }
  .holiday-calendar .day {
    position: relative;
    aspect-ratio: 1 / 1; display: flex; align-items: center; justify-content: center;
    font-size: 0.78rem; border-radius: 8px; cursor: pointer; border: 1px solid transparent;
    transition: background .12s, border-color .12s;
  }
  .holiday-calendar .day:hover { border-color: ${NAVY}; }
  .holiday-calendar .day.empty { visibility: hidden; cursor: default; }
  .holiday-calendar .day.weekend { background: #f2f4f8; color: #98a1b2; }
  .holiday-calendar .day.manual { background: ${ORANGE}; color: #fff; font-weight: 600; }
  .holiday-calendar .day.fixed {
    background: ${NAVY}; color: #fff; font-weight: 600; cursor: not-allowed;
  }
  .holiday-calendar .day.fixed:hover { border-color: transparent; }

  /* Aralık seçimi: başlangıç günü ve devam eden seçim önizlemesi */
  .holiday-calendar .day.range-start {
    outline: 2px solid ${NAVY}; outline-offset: -2px; font-weight: 700;
  }
  .holiday-calendar .day.range-preview { background: #dbe4f5; color: ${NAVY}; }

  /* İşaretli günlerin açıklaması: yerleşik title yerine anında görünen balon.
     Ay kartı taşmayı kırpmasın diye overflow açık bırakıldı. */
  .holiday-calendar .month-card { overflow: visible; }
  .holiday-calendar .day[data-tip]::after {
    content: attr(data-tip);
    position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
    background: #111a2b; color: #fff; padding: 5px 9px; border-radius: 7px;
    font-size: 0.72rem; font-weight: 500; line-height: 1.25; white-space: nowrap;
    opacity: 0; visibility: hidden; transition: opacity .12s; pointer-events: none; z-index: 30;
    box-shadow: 0 4px 14px rgba(0,0,0,.18);
  }
  .holiday-calendar .day[data-tip]::before {
    content: ""; position: absolute; bottom: calc(100% + 1px); left: 50%;
    transform: translateX(-50%); border: 5px solid transparent; border-top-color: #111a2b;
    opacity: 0; visibility: hidden; transition: opacity .12s; pointer-events: none; z-index: 30;
  }
  .holiday-calendar .day[data-tip]:hover::after,
  .holiday-calendar .day[data-tip]:hover::before { opacity: 1; visibility: visible; }

  /* Seçim modu düğmeleri: aktif olan lacivert DOLU KALIR.
     Bootstrap'ın .btn sınıfı kullanılmıyor — reactstrap Button'ın ürettiği
     .btn/.btn-secondary kuralları :hover, :focus ve :active durumlarında kendi
     arka planını dayatıp seçili rengi tıklamadan sonra siliyordu. Sade <button>
     ile o kaskad hiç devreye girmiyor; renk yalnız moda bağlı. */
  .holiday-calendar .holiday-mode-group { display: inline-flex; }
  .holiday-calendar .holiday-mode-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid rgba(10, 49, 110, .25); background: #fff; color: ${NAVY};
    font-size: 0.8rem; font-weight: 500; line-height: 1.2; padding: 7px 16px;
    cursor: pointer; transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
  }
  .holiday-calendar .holiday-mode-btn:first-child {
    border-radius: 999px 0 0 999px; border-right-width: 0;
  }
  .holiday-calendar .holiday-mode-btn:last-child { border-radius: 0 999px 999px 0; }
  .holiday-calendar .holiday-mode-btn:hover:not(.is-on) {
    background: rgba(10, 49, 110, .07); border-color: rgba(10, 49, 110, .45);
  }
  .holiday-calendar .holiday-mode-btn:focus-visible {
    outline: 2px solid ${NAVY}; outline-offset: 2px;
  }
  /* Aktif mod — hover/focus/active fark etmeksizin turuncu kalır. */
  .holiday-calendar .holiday-mode-btn.is-on,
  .holiday-calendar .holiday-mode-btn.is-on:hover,
  .holiday-calendar .holiday-mode-btn.is-on:focus,
  .holiday-calendar .holiday-mode-btn.is-on:active {
    background: ${NAVY}; border-color: ${NAVY}; color: #fff; font-weight: 600;
    box-shadow: 0 2px 8px rgba(10, 49, 110, .28);
  }
  .holiday-calendar .holiday-mode-btn.is-on:first-child { border-right-width: 1px; }
  .holiday-calendar .holiday-mode-btn.is-on + .holiday-mode-btn { border-left-width: 0; }

  .holiday-calendar .legend-dot {
    width: 12px; height: 12px; border-radius: 4px; display: inline-block; margin-right: 6px;
  }
  .holiday-calendar .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
  .holiday-calendar .btn-navy:hover { filter: brightness(1.08); color: #fff; }
  /* Hazır açıklama çipleri — projenin laciverdi ile aynı aileden.
     DİKKAT: bu kurallar ".holiday-calendar" ile ÖNEKLENEMEZ. reactstrap Modal
     içeriğini portal ile document.body altına taşır, yani çipler bileşenin
     sarmalayıcısının dışında kalır ve önekli seçici hiç eşleşmez (çipler bu
     yüzden gri kalıyordu). Sınıf adı çakışmasın diye "holiday-" ile başlar. */
  button.holiday-suggestion {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid rgba(10, 49, 110, .18); background: rgba(10, 49, 110, .06);
    color: ${NAVY}; border-radius: 999px; padding: 6px 14px;
    font-size: 0.8rem; font-weight: 500; line-height: 1.2; cursor: pointer;
    transition: background .14s, border-color .14s, box-shadow .14s, color .14s;
  }
  button.holiday-suggestion i { font-size: 0.95rem; opacity: .75; }
  button.holiday-suggestion:hover {
    background: rgba(10, 49, 110, .12); border-color: rgba(10, 49, 110, .38);
  }
  button.holiday-suggestion:focus-visible {
    outline: 2px solid ${NAVY}; outline-offset: 2px;
  }
  button.holiday-suggestion.active {
    background: ${NAVY}; border-color: ${NAVY}; color: #fff;
    box-shadow: 0 2px 8px rgba(10, 49, 110, .28);
  }
  button.holiday-suggestion.active i { opacity: 1; }
  .holiday-suggestion-label {
    font-size: 0.75rem; font-weight: 600; letter-spacing: .02em;
    color: #7a869c; text-transform: uppercase;
  }
`;

/** Yerel tarih → YYYY-MM-DD. toISOString() UTC'ye çevirip günü kaydırırdı. */
const toIsoDate = (year, monthIndex, day) =>
    `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** Ayın hücreleri: baştaki boşluklar + gün numaraları (pazartesi başlangıçlı). */
const buildMonthCells = (year, monthIndex) => {
    const firstDay = new Date(year, monthIndex, 1).getDay();       // 0 = Pazar
    const leading = (firstDay + 6) % 7;                            // pazartesi başlangıcına çevir
    const dayCount = new Date(year, monthIndex + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < leading; i += 1) cells.push(null);
    for (let d = 1; d <= dayCount; d += 1) cells.push(d);
    return cells;
};

/** İki tarih arasındaki tüm günler (iki uç dahil), YYYY-MM-DD listesi. */
const datesBetween = (fromIso, toIso) => {
    const [start, end] = fromIso <= toIso ? [fromIso, toIso] : [toIso, fromIso];
    const result = [];
    const cursor = new Date(`${start}T00:00:00`);
    const last = new Date(`${end}T00:00:00`);
    while (cursor <= last) {
        result.push(toIsoDate(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()));
        cursor.setDate(cursor.getDate() + 1);
    }
    return result;
};

const formatTr = (iso) => {
    const [y, m, d] = iso.split("-");
    return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
};

/**
 * Resmî tatil takvimi — süper adminin valör hesabında atlanacak günleri
 * takvimden işaretlediği ekran.
 *
 * <p>Sabit tarihli millî bayramlar (1 Ocak, 23 Nisan, 29 Ekim…) sunucuda
 * ÜRETİLİR; burada lacivert ve kilitli görünürler. Elle girilmesi gereken günler
 * hicri takvime bağlı dini bayramlar, köprü günleri ve olağanüstü ilan edilen
 * idari tatillerdir — turuncu gösterilir ve tıklanarak kaldırılabilir.</p>
 *
 * <p>Bayramlar blok hâlinde ilan edildiği için "Aralık" modunda başlangıç ve
 * bitiş günü seçilir; aradaki bütün günler tek işlemde işaretlenir.</p>
 */
export default function HolidayCalendarTab() {
    const {
        year,
        setYear,
        holidayByDate,
        yearStatus,
        loading,
        saving,
        addHoliday,
        addHolidayRange,
        removeHoliday,
        toggleConfirmation,
    } = usePublicHolidays();

    const [rangeMode, setRangeMode] = useState(false);
    const [rangeStart, setRangeStart] = useState(null);   // aralık modunda ilk tıklanan gün
    const [hoverDate, setHoverDate] = useState(null);     // seçim önizlemesi için

    const [modal, setModal] = useState(null); // { dates: [], existing }
    const [description, setDescription] = useState("");
    const [numberDays, setNumberDays] = useState(true);

    // useCallback: effect'in bağımlılığı, her render'da dinleyicinin
    // sökülüp yeniden bağlanmaması için sabit kalmalı.
    const cancelRange = useCallback(() => {
        setRangeStart(null);
        setHoverDate(null);
    }, []);

    /**
     * Yarım kalmış aralık seçimini boşluğa tıklayınca (veya Esc ile) iptal et.
     *
     * <p>Dinleyici yalnız seçim beklerken bağlanır ve effect, seçimi başlatan
     * tıklama olayı tamamlandıktan SONRA çalıştığı için o tıklamayı yakalamaz —
     * aksi hâlde başlangıç günü seçilir seçilmez iptal olurdu. Gün hücresine ve
     * modal içine yapılan tıklamalar akışın parçasıdır, iptal etmezler.</p>
     */
    useEffect(() => {
        if (!rangeStart) return undefined;

        const onDocumentClick = (event) => {
            if (event.target.closest(".holiday-calendar .day")) return;
            if (event.target.closest(".modal")) return;
            cancelRange();
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") cancelRange();
        };

        document.addEventListener("click", onDocumentClick);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("click", onDocumentClick);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [rangeStart, cancelRange]);

    const manualCount = useMemo(
        () => Object.values(holidayByDate).filter((h) => !h.fixed).length,
        [holidayByDate]
    );

    /** Aralık önizlemesinde boyanacak günler (henüz kaydedilmedi). */
    const previewDates = useMemo(() => {
        if (!rangeMode || !rangeStart || !hoverDate) return new Set();
        return new Set(datesBetween(rangeStart, hoverDate));
    }, [rangeMode, rangeStart, hoverDate]);

    const openSingle = (date) => {
        const existing = holidayByDate[date];
        setDescription(existing?.description || "");
        setModal({ dates: [date], existing: existing || null });
    };

    const openRange = (fromIso, toIso) => {
        // Sabit tarihli günler sunucuda reddedilir; aralıktan sessizce çıkarılır
        // ki 20-30 Ekim gibi bir seçim 29 Ekim yüzünden tamamen başarısız olmasın.
        const all = datesBetween(fromIso, toIso);
        const selectable = all.filter((d) => !holidayByDate[d]?.fixed);
        const skipped = all.length - selectable.length;

        setDescription("");
        setModal({ dates: selectable, existing: null, skipped });
    };

    const handleDayClick = (date) => {
        if (holidayByDate[date]?.fixed) return; // üretilen gün: değiştirilemez

        if (!rangeMode) {
            openSingle(date);
            return;
        }
        if (!rangeStart) {
            setRangeStart(date);
            return;
        }
        openRange(rangeStart, date);
        cancelRange();
    };

    const closeModal = () => {
        setModal(null);
        setDescription("");
        cancelRange();
    };

    const handleSave = async () => {
        const text = description.trim();
        if (!text || !modal?.dates.length) return;

        if (modal.dates.length === 1) {
            const ok = await addHoliday(modal.dates[0], text);
            if (ok) closeModal();
            return;
        }
        const items = modal.dates.map((date, index) => ({
            holidayDate: date,
            description: numberDays ? `${text} ${index + 1}. Gün` : text,
        }));
        const ok = await addHolidayRange(items);
        if (ok) closeModal();
    };

    const handleRemove = async () => {
        const ok = await removeHoliday(modal.dates[0]);
        if (ok) closeModal();
    };

    const isRange = (modal?.dates.length || 0) > 1;

    return (
        <div className="holiday-calendar">
            <style>{styles}</style>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div>
                    <h3 className="mb-1">Resmî Tatil Takvimi</h3>
                    <div className="text-muted small">
                        Valör (yatış) tarihi hafta sonuna ya da bu günlere denk gelirse bir sonraki iş gününe kayar.
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <Button
                        size="sm"
                        className="btn-outline-navy btn-rounded"
                        onClick={() => setYear(year - 1)}
                        disabled={loading}
                    >
                        <i className="bx bx-chevron-left"></i>
                    </Button>
                    <span className="fw-bold fs-5" style={{ color: NAVY, minWidth: 64, textAlign: "center" }}>
                        {year}
                    </span>
                    <Button
                        size="sm"
                        className="btn-outline-navy btn-rounded"
                        onClick={() => setYear(year + 1)}
                        disabled={loading}
                    >
                        <i className="bx bx-chevron-right"></i>
                    </Button>
                </div>
            </div>

            {/* Yıl onayı: eksik takvim sessizce yanlış valör üretir, uyarı burada durur. */}
            {yearStatus?.confirmed ? (
                <Alert color="success" className="d-flex flex-wrap justify-content-between align-items-center border-0 shadow-sm">
                    <span>
                        <i className="bx bx-check-circle me-2"></i>
                        {year} takvimi onaylandı
                        {yearStatus.confirmedBy ? ` — ${yearStatus.confirmedBy}` : ""}.
                    </span>
                    <Button size="sm" color="link" className="text-decoration-none"
                            onClick={toggleConfirmation} disabled={saving}>
                        Onayı kaldır
                    </Button>
                </Alert>
            ) : (
                <Alert color="warning" className="d-flex flex-wrap justify-content-between align-items-center border-0 shadow-sm">
                    <span>
                        <i className="bx bx-error me-2"></i>
                        {year} takvimi henüz onaylanmadı. Dini bayram ve köprü günler eksikse
                        valör tarihleri olduğundan erken hesaplanır ve tutar yanlış aya yazılır.
                    </span>
                    <Button size="sm" className="btn-navy btn-rounded"
                            onClick={toggleConfirmation} disabled={saving || loading}>
                        <i className="bx bx-check me-1"></i> Takvimi Onayla
                    </Button>
                </Alert>
            )}

            {/* Seçim modu: bayramlar blok hâlinde ilan edildiği için aralık seçimi ayrı bir mod. */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div className="holiday-mode-group" role="group" aria-label="Seçim modu">
                    <button
                        type="button"
                        className={`holiday-mode-btn${rangeMode ? "" : " is-on"}`}
                        aria-pressed={!rangeMode}
                        onClick={() => { setRangeMode(false); cancelRange(); }}
                    >
                        <i className="bx bx-calendar-check"></i> Tek gün
                    </button>
                    <button
                        type="button"
                        className={`holiday-mode-btn${rangeMode ? " is-on" : ""}`}
                        aria-pressed={rangeMode}
                        onClick={() => setRangeMode(true)}
                    >
                        <i className="bx bx-calendar-plus"></i> Aralık seç
                    </button>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3 small text-muted">
                    <span>
                        <span className="legend-dot" style={{ background: NAVY }}></span>
                        Millî bayram (otomatik)
                    </span>
                    <span>
                        <span className="legend-dot" style={{ background: ORANGE }}></span>
                        Elle girilen ({manualCount} gün)
                    </span>
                    <span>
                        <span className="legend-dot" style={{ background: "#f2f4f8" }}></span>
                        Hafta sonu
                    </span>
                </div>
            </div>

            {rangeMode && (
                <div className="alert alert-light border d-flex align-items-center gap-2 py-2">
                    <i className="bx bx-info-circle" style={{ color: NAVY }}></i>
                    {rangeStart ? (
                        <span>
                            Başlangıç: <strong>{formatTr(rangeStart)}</strong> — şimdi bitiş gününü seçin.
                            <Button size="sm" color="link" className="text-decoration-none py-0"
                                    onClick={cancelRange}>
                                vazgeç
                            </Button>
                            <span className="text-muted ms-1">
                                (boşluğa tıklamak veya Esc de iptal eder)
                            </span>
                        </span>
                    ) : (
                        <span>Aralığın <strong>başlangıç</strong> gününü seçin; ardından bitiş gününü tıklayın.</span>
                    )}
                </div>
            )}

            {loading ? (
                <div className="text-center p-5">
                    <Spinner color="primary" size="sm" />
                    <span className="ms-2 text-muted">Takvim yükleniyor...</span>
                </div>
            ) : (
                <div className="row g-3">
                    {MONTHS.map((monthName, monthIndex) => (
                        <div className="col-12 col-md-6 col-lg-4 col-xxl-3" key={monthName}>
                            <div className="month-card h-100">
                                <div className="month-title">{monthName}</div>
                                <div className="day-grid">
                                    {WEEKDAYS.map((w) => (
                                        <div className="weekday" key={w}>{w}</div>
                                    ))}
                                    {buildMonthCells(year, monthIndex).map((day, cellIndex) => {
                                        if (day === null) {
                                            // eslint-disable-next-line react/no-array-index-key
                                            return <div className="day empty" key={`empty-${cellIndex}`} />;
                                        }
                                        const date = toIsoDate(year, monthIndex, day);
                                        const holiday = holidayByDate[date];
                                        const weekday = new Date(year, monthIndex, day).getDay();
                                        const isWeekend = weekday === 0 || weekday === 6;

                                        const classes = ["day"];
                                        if (holiday?.fixed) classes.push("fixed");
                                        else if (holiday) classes.push("manual");
                                        else if (previewDates.has(date)) classes.push("range-preview");
                                        else if (isWeekend) classes.push("weekend");
                                        if (rangeStart === date) classes.push("range-start");

                                        // İşaretli her gün açıklamasını balon olarak gösterir.
                                        const tip = holiday
                                            ? `${holiday.description}${holiday.fixed ? " • otomatik" : ""}`
                                            : undefined;

                                        return (
                                            <div
                                                key={date}
                                                className={classes.join(" ")}
                                                data-tip={tip}
                                                onClick={() => handleDayClick(date)}
                                                onMouseEnter={() => rangeMode && rangeStart && setHoverDate(date)}
                                                role="button"
                                                tabIndex={holiday?.fixed ? -1 : 0}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") handleDayClick(date);
                                                }}
                                            >
                                                {day}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal isOpen={!!modal} toggle={closeModal} centered>
                <ModalHeader toggle={closeModal}>
                    {modal?.existing ? "Tatil Günü" : isRange ? "Tatil Aralığı Ekle" : "Tatil Günü Ekle"}
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <Badge style={{ background: NAVY, borderRadius: 999 }} className="fw-normal">
                            {isRange
                                ? `${formatTr(modal.dates[0])} – ${formatTr(modal.dates[modal.dates.length - 1])} (${modal.dates.length} gün)`
                                : modal && formatTr(modal.dates[0])}
                        </Badge>
                        {modal?.skipped > 0 && (
                            <div className="text-muted small mt-2">
                                <i className="bx bx-info-circle me-1"></i>
                                {modal.skipped} gün atlandı — sabit tarihli millî bayramlar zaten otomatik takvimde.
                            </div>
                        )}
                    </div>

                    <Label for="holiday-description">Açıklama</Label>
                    <Input
                        id="holiday-description"
                        value={description}
                        maxLength={120}
                        placeholder={isRange ? "Örn. Kurban Bayramı" : "Örn. Kurban Bayramı 1. Gün"}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    <div className="holiday-suggestion-label mt-3 mb-2">Hazır açıklamalar</div>
                    <div className="d-flex flex-wrap gap-2">
                        {SUGGESTIONS.map((s) => {
                            const active = description.trim() === s;
                            return (
                                <button
                                    type="button"
                                    key={s}
                                    className={`holiday-suggestion${active ? " active" : ""}`}
                                    onClick={() => setDescription(s)}
                                    aria-pressed={active}
                                >
                                    <i className={active ? "bx bx-check" : "bx bx-calendar-event"}></i>
                                    {s}
                                </button>
                            );
                        })}
                    </div>

                    {isRange && (
                        <FormGroup check className="mt-3">
                            <Input
                                type="checkbox"
                                id="number-days"
                                checked={numberDays}
                                onChange={(e) => setNumberDays(e.target.checked)}
                            />
                            <Label check for="number-days">
                                Günleri numaralandır
                                <span className="text-muted small ms-1">
                                    (“{description.trim() || "Kurban Bayramı"} 1. Gün”, “2. Gün”…)
                                </span>
                            </Label>
                        </FormGroup>
                    )}

                    <div className="text-muted small mt-3">
                        {isRange ? "Bu günlerin hiçbiri" : "Bu gün"} valör hesabında iş günü sayılmaz;
                        yatış tarihi bir sonraki iş gününe kayar.
                    </div>
                </ModalBody>
                <ModalFooter>
                    {modal?.existing && (
                        <Button color="danger" className="btn-rounded me-auto"
                                onClick={handleRemove} disabled={saving}>
                            <i className="bx bx-trash me-1"></i> Kaldır
                        </Button>
                    )}
                    <Button color="light" className="btn-rounded" onClick={closeModal} disabled={saving}>
                        Vazgeç
                    </Button>
                    <Button className="btn-navy btn-rounded" onClick={handleSave}
                            disabled={saving || !description.trim() || !modal?.dates.length}>
                        {saving ? <Spinner size="sm" /> : <i className="bx bx-check me-1"></i>}
                        {modal?.existing ? " Güncelle" : isRange ? ` ${modal.dates.length} Günü Ekle` : " Ekle"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
